import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { jwtTokenService } from '@mathflat/shared/@modules/auth/jwtTokenService'
import axios, { type AxiosError } from 'axios'
import { isObjectLike } from 'lodash-es'
import qs from 'qs'

import { TOAST_STR } from '@/constant'
import { CommonError } from '@/dto'
import WebviewService from '@/service/webview.service.ts'
import type { ResErr } from '@/types/common/api'
import { deleteNullField } from '@/utils/common'
import { envUtils } from '@/utils/envUtils.ts'
import { reporter } from '@/utils/modules/errorReporting/reporter'
import { toastError } from '@/utils/toast'

import { wrapErrorHandlerWithJwt, wrapRequestInterceptorWithJwt } from './jwtInterceptors'

const BASE_URL = (() => {
  if (envUtils.isLive) {
    return `https://ai.${domainProvider.TARGET_DOMAIN}`
  }
  if (envUtils.isStaging) {
    return `https://ai-staging.${domainProvider.TARGET_DOMAIN}`
  }

  return `https://ai-dev.${domainProvider.TARGET_DOMAIN}`
})()

const XPlatform = (() => {
  if (WebviewService.isAndroid) return 'TEACHER_ANDROID'
  if (WebviewService.isIpad) return 'TEACHER_IOS'
  return 'TEACHER_WEB'
})()
export const aiAxios = axios.create({
  baseURL: BASE_URL,
  headers: {
    'x-platform': XPlatform,
  },
  paramsSerializer: (params) => {
    // params가 null인 값은 서버로 보내지 않음
    const newParams = isObjectLike(params) ? deleteNullField(params) : params
    return qs.stringify(newParams, { arrayFormat: 'comma' })
  },
})

aiAxios.interceptors.request.use(wrapRequestInterceptorWithJwt((config) => config))

aiAxios.interceptors.response.use(
  (response) => response,
  wrapErrorHandlerWithJwt(
    async (error: unknown) => {
      const axiosError = error as AxiosError<ResErr>

      if (axiosError.response) {
        const {
          config,
          data: { message, code, data },
          status,
        } = axiosError.response
        const err = new CommonError(message, { type: 'API', status, code, data, config })

        if (status === 500) {
          toastError(TOAST_STR.errorMessage)
        }

        reporter.capture(err, { mechanism: 'interceptor' })
        return Promise.reject<CommonError>(err)
      }

      reporter.capture(axiosError, { mechanism: 'interceptor' })
      return Promise.reject(axiosError)
    },
    async (originalRequest, newAccessToken) => {
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      return aiAxios(originalRequest)
    },
  ),
)

export const fetchSSE = async (
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: { params?: Record<string, any> } & RequestInit,
) => {
  const { params, ...otherOptions } = options

  const filteredParams = params && isObjectLike(params) ? deleteNullField(params) : options.params
  const queryString = params ? `?${qs.stringify(filteredParams, { arrayFormat: 'comma' })}` : ''

  const accessToken = jwtTokenService.getAccessToken()

  return fetch(`${aiAxios.defaults.baseURL}${url}${queryString}`, {
    method: 'POST',
    headers: {
      'x-platform': XPlatform,
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      'Content-Type': 'application/json; charset=utf-8',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
    ...otherOptions,
  })
}
